// Input form elements such as text fields and checkboxes.
import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { fontFamilySansMedium, fontFamilySansRegular, TextField, UtilityText } from "./text";
import { HorizBox, HoverView, Pad, PadBox } from "./basics";
import { colorBorderPrimary, colorButtonAccent, colorButtonPrimary, colorButtonSecondary, colorGreyFormHover, colorGreyFormPress, colorGreyHoverBorder, colorSurfaceSecondary, colorTagHighlight, colorTextPrimary, colorTextSecondary, colorTextWarning } from "./color";
import { Checkbox as CheckboxOpen, CheckboxCheckedFilled, ChevronDown, ChevronUp, RadioButton, RadioButtonChecked, Search, Edit as EditIcon, Close as CloseIcon, Checkmark as CheckmarkIcon } from '@carbon/icons-react';
import { useTranslation } from "./translation";
import { undefA, undefR, undefS, undefTL, makeTestID, undefE } from "util/stdtypes";
import { usePersonaKey } from "util/datastore";
import { getFontSize } from "platform-specific/font";
import { ProfilePhoto } from "./people";


export function FormField({label = undefTL, text = undefS, descriptionLabel = undefTL, descriptionText = undefS, errorLabel = undefTL, errorText = undefS, required = false, accessoryAction = undefE, children}) {
    return <View>
        <HorizBox center>
            <UtilityText type='small' weight="medium" label={label} text={text}/>
            {required && <UtilityText type='small' text='*' color={colorTextWarning} />}
            {accessoryAction && <View style={{marginLeft: 'auto'}}>{accessoryAction}</View>}
        </HorizBox>
        <Pad size={8} />
        {children}
        {(errorLabel || errorText) && <PadBox top={8}>
            <UtilityText type='small' label={errorLabel} text={errorText} color={colorTextWarning} />
        </PadBox>}
        {(descriptionLabel || descriptionText) && <PadBox top={8}>
            <UtilityText type='small' label={descriptionLabel} text={descriptionText} color={colorTextSecondary} />
        </PadBox>}
    </View>
}


const RadioContext = React.createContext({radioSelection: null, onSetRadio: undefA});

export function RadioGroup({children, value, onChange}) {
    const [radioSelection, setRadioSelection] = React.useState(value);

    function onSetRadio(value) {
        setRadioSelection(value);
        onChange(value);
    }
    return <RadioContext.Provider value={{radioSelection, onSetRadio}}>
        {children}
    </RadioContext.Provider>
}

export function RadioOption({emoji = undefS, text = undefS, radioKey = undefS, label = undefTL}) {
    const s = RadioItemStyle;
    const {radioSelection, onSetRadio} = React.useContext(RadioContext);
    const value = radioSelection === radioKey;
    return <HoverView hoverStyle={s.hover} pressedStyle={s.pressed} testID={makeTestID({label, text})}
        onPress={() => onSetRadio(radioKey)} role='checkbox'>
        <PadBox vert={8}>
            <HorizBox center>
                {value ? <RadioButtonChecked size={32} color={colorButtonPrimary}/> : <RadioButton size={32} style={{fill: colorBorderPrimary}} />}
                <Pad size={12} />
                {emoji && <PadBox right={6}><UtilityText aria-hidden text={emoji} type='tiny' weight='strong' /></PadBox>}
                <UtilityText text={text} label={label} />
            </HorizBox>
        </PadBox>
    </HoverView>
}
const RadioItemStyle = StyleSheet.create({
    hover: {
        backgroundColor: colorGreyFormHover
    },
    pressed: {
        backgroundColor: colorGreyFormPress    
    }
})


export function Checkbox({emoji=undefS, text=undefS, label = undefTL, value=false, onChange=undefA, size=32, ariaLabel = undefTL, testID=undefS}) {
    const s = CheckboxStyle;
    return <HoverView hoverStyle={s.hover} pressedStyle={s.pressed} testID={makeTestID({testID, label, text})}
        onPress={() => onChange(!value)} role='checkbox' ariaLabel={ariaLabel}>
        <PadBox vert={8}>
            <HorizBox center>
                <View style={{width:size}}>
                    {value ? <CheckboxCheckedFilled size={size} color={colorButtonPrimary}/> : <CheckboxOpen size={size} style={{fill: colorBorderPrimary}} />}
                </View>
                {(text || label) ? <Pad size={12} /> : null}
                {emoji && <PadBox right={6}><UtilityText aria-hidden text={emoji} type='tiny' weight='strong' /></PadBox>}
                <UtilityText text={text} label={label} />
            </HorizBox>
        </PadBox>
    </HoverView>
}
const CheckboxStyle = StyleSheet.create({
    hover: {
        backgroundColor: colorGreyFormHover
    },
    pressed: {
        backgroundColor: colorGreyFormPress    
    }
})


export function AccordionField({titleContent, testID=undefS, defaultOpen=false, forceOpen=false, children}) {
    const s = AccordionFieldStyle;
    const [open, setOpen] = React.useState(defaultOpen);
    return <View>
        <HoverView style={s.titleBar} testID={testID} hoverStyle={s.hover} pressedStyle={s.pressed} onPress={() => setOpen(!open)}>
            <HorizBox center spread>
                {titleContent}
                <Pad />
                {!forceOpen && (open ? <ChevronUp color={colorTextPrimary}/> : <ChevronDown color={colorTextPrimary}/>)}
            </HorizBox>
        </HoverView>
        {(open || forceOpen) && children}
    </View>

}
const AccordionFieldStyle = StyleSheet.create({
    titleBar: {
        minHeight: 48,
        justifyContent: 'center',
        paddingRight: 16
        // paddingHorizontal: 16
    }, 
    hover: {
        backgroundColor: colorGreyFormHover,
    },
    pressed: {
        backgroundColor: colorGreyFormPress    
    }
});


export function Toggle({emoji = undefS, text = undefS, label = undefTL, value = false, spread = false, onChange = undefA, testID = undefS}) {
    const s = ToggleStyle;
    const tLabel = useTranslation(label);
    return <HoverView hoverStyle={s.hover} testID={makeTestID({testID, label, text})}
        onPress={() => onChange(!value)} role='button' shrink
        ariaText={value ? `${text ?? tLabel}, checked` : `${text ?? tLabel}, unchecked`}
    >
        <PadBox vert={8}>
            <HorizBox center spread={spread} shrink>        
                <HorizBox center shrink>
                    {emoji && <PadBox right={8}><UtilityText aria-hidden text={emoji} type='tiny' weight='strong' /></PadBox>}
                    <UtilityText text={text} label={label} />
                </HorizBox>
                <Pad size={12} />
                <View style={value ? s.toggleZoneSelected : s.toggleZone} >
                    <View style={value ? s.toggleBallSelected : s.toggleBall} />
                </View>
            </HorizBox>
        </PadBox>
    </HoverView>
}

const ToggleStyle = StyleSheet.create({
    toggleZone: {
        width: 56,
        height: 32,
        backgroundColor: colorBorderPrimary,
        borderRadius: 100,
        ...{transition: 'background-color 0.2s ease-in-out'} as any
    },
    toggleBall: {
        boxShadow: '2px 0px 10px rgba(0, 0, 0, 0.30)' as any, // FIXME: boxShadow not typed for native
        elevation: 5,  // for Android,
        position: 'absolute',
        left: 2,
        top: 2,
        width: 28,
        height: 28,
        borderRadius: 100,
        backgroundColor: colorButtonSecondary,
        ...{transition: 'left 0.2s ease-in-out, background-color 0.2s ease-in-out'} as any
    },
    toggleZoneSelected: {
        width: 56,
        height: 32,
        backgroundColor: colorTagHighlight,
        borderRadius: 100,
        ...{transition: 'background-color 0.2s ease-in-out'} as any
    },
    toggleBallSelected: {
        backgroundColor: colorButtonAccent,
        left: 56-28-2,
        top: 2,
        width: 28,
        height: 28,
        borderRadius: 100,
        ...{transition: 'left 0.2s ease-in-out, background-color 0.2s ease-in-out'} as any
    },
    hover: {
        backgroundColor: colorSurfaceSecondary,
    },
    pressed: {
        backgroundColor: colorGreyFormPress,
        borderTopRightRadius: 100,
        borderBottomRightRadius: 100,
    }
})

export function SearchField(props) {
    return <TextField multiline={false} icon={Search} {...props} />
}


export function TextFieldButton({placeholder=undefTL, testID=undefS, noTranslationPlaceholder=undefS, placeholderParams=undefR, onPress=undefA}) {
    const s = TextFieldButtonStyle;
    const tPlaceholder = useTranslation(placeholder, placeholderParams);
    const personaKey = usePersonaKey();
    return <HoverView role='button' style={s.textFieldButton} ariaLabel={placeholder} hoverStyle={s.hover} onPress={onPress} testID={testID}>
        <HorizBox center>
            {personaKey && <PadBox right={10}><ProfilePhoto userId={personaKey} type='tiny' /></PadBox>}
            <Text style={s.textFieldText}>{noTranslationPlaceholder ??tPlaceholder}</Text>
        </HorizBox>
        <EditIcon size={24} color={colorTextPrimary} style={{ flexShrink: 0, paddingLeft: 16 }}/>
    </HoverView>
}
const TextFieldButtonStyle = StyleSheet.create({
    textFieldText: {
        color: colorTextPrimary, 
        fontFamily: fontFamilySansMedium,
        fontSize: getFontSize(14),
    },
    textFieldButton: {
        borderColor: colorBorderPrimary,
        backgroundColor: colorButtonSecondary,
        borderWidth: 1,
        borderRadius: 32,
        paddingHorizontal: 16,
        height: 50,
        fontFamily: fontFamilySansRegular,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        overflow: "hidden"
    },
    hover: {
        borderColor: colorGreyHoverBorder
    }
})
